import { formatBannerTitle } from '../../utils/bannerTitleFormat';
import { readUsernameFromPayload, stripUsernameAt } from '../../utils/usernameFormat';
import { PUSH_CONFIG } from './pushConstants';
import {
  isIncomingCallRequestPush,
  isMissedCallPush,
} from './pushCallNotificationPolicy';
import { normalizePushNotificationBody } from './pushNotificationText';
import {
  flattenPushDataPayload,
  resolvePushBody,
  resolvePushLink,
  resolvePushTitle,
} from './pushPayloadUtils';

const OPEN_APP_RESPOND_SUFFIX_RE = /\.?\s*open the app to respond\.?\s*$/i;

const resolveCallMediaKind = (callType) => {
  const normalized = String(callType || '').toLowerCase();
  return normalized === 'video' ? 'video' : 'voice';
};

const stripOpenAppRespondSuffix = (body) =>
  String(body || '')
    .replace(OPEN_APP_RESPOND_SUFFIX_RE, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

/**
 * Foreground toasts are in-app only — use call-type titles and drop background CTA copy.
 */
const buildCallForegroundPushCopy = ({ pushData, username, resolvedBody }) => {
  if (!isIncomingCallRequestPush(pushData)) {
    return null;
  }

  const mediaKind = resolveCallMediaKind(pushData?.callType);
  const mediaLabel = mediaKind === 'video' ? 'Video' : 'Voice';
  const isMissed = isMissedCallPush(pushData);
  const title = isMissed ? `Missed ${mediaLabel} Call` : `Incoming ${mediaLabel} Call`;

  let body = stripOpenAppRespondSuffix(resolvedBody);
  if (!body && username) {
    body = isMissed
      ? `${username} tried to start a ${mediaKind} call`
      : `${username} wants to start a ${mediaKind} call`;
  }

  return { title, body };
};

const formatTipAmount = (amount) => {
  if (amount == null || amount === '') return '';
  const amountText = String(amount).trim();
  const amountNumber = Number(amountText);
  if (!Number.isFinite(amountNumber)) return amountText;
  if (Number.isInteger(amountNumber)) return amountNumber.toLocaleString('en-US');

  const hasSingleDecimal = /^\d+\.\d$/.test(amountText);
  if (hasSingleDecimal) return amountNumber.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return amountNumber.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCurrencyAmount = (amount) => {
  if (amount == null || amount === '') return '';
  const amountNumber = Number(amount);
  if (!Number.isFinite(amountNumber)) return '';
  return amountNumber.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const FREE_TRIAL_PROMOTION_EVENTS = new Set([
  'free_trial_active',
  'free_trial_canceled',
  'promo_trial_resubscribe',
  'promo_trial_renewal',
]);

const DISCOUNT_PROMOTION_EVENTS = new Set(['discount_active']);

const buildFallbackPushContent = ({
  type,
  eventType,
  username,
  amount,
  trialDays,
  offerSlots,
  discountedPrice,
  percentOff,
}) => {
  if (eventType === 'new_follower' || type === 'creator_followers') {
    return {
      title: 'New Follower 🎉',
      body: username ? `${username} just followed you` : 'You have a new follower',
    };
  }

  if (eventType === 'call_credits' || type === 'call_credits') {
    return {
      title: 'Call Credits',
      body: 'Your call credits were updated',
    };
  }

  if (eventType === 'content_unlocked' || type === 'content_unlocked') {
    return {
      title: 'Unlocked 🎉',
      body: username
        ? `Enjoy your new exclusive content from ${username}`
        : 'Enjoy your new exclusive content',
    };
  }

  if (type === 'new_content') {
    return {
      title: 'New Content Available',
      body: username ? `${username} has posted new content` : 'A creator posted new content',
    };
  }

  const isTipType =
    type === 'payment_tip' ||
    type === 'tip' ||
    type === 'creator_sales_tips' ||
    eventType === 'tip_received' ||
    eventType === 'tip_sent';

  if (isTipType && eventType === 'tip_received') {
    const formattedAmount = formatTipAmount(amount);
    return {
      title: 'Tip received',
      body:
        username && formattedAmount
          ? `${username} sent you $${formattedAmount}`
          : username
            ? `${username} sent you a tip`
            : 'You received a new tip',
    };
  }

  const isDiscountType = eventType === 'discount_active' || type === 'discount_active';

  if (isDiscountType) {
    const price = formatCurrencyAmount(discountedPrice);
    const percent = Number(percentOff);
    const hasPercent = Number.isFinite(percent) && percent > 0;
    const discountText =
      price && hasPercent
        ? `, resubscribe for $${price} (${percent}% off)`
        : price
          ? `, resubscribe for $${price}`
          : hasPercent
            ? ` (${percent}% off)`
            : '';

    return {
      title: 'Exclusive discount 🎁',
      body: username
        ? `${username} lowered their price just for you${discountText}`
        : price
          ? `A discount is active, resubscribe for $${price}`
          : 'A discount is active for you',
    };
  }

  const isFreeTrialType =
    FREE_TRIAL_PROMOTION_EVENTS.has(eventType) ||
    (type === 'promotion' && trialDays != null);

  if (isFreeTrialType) {
    const days = Number(trialDays);
    const slots = Number(offerSlots);
    const hasDays = Number.isFinite(days) && days > 0;
    const hasSlots = Number.isFinite(slots) && slots > 0;
    const daysText = hasDays ? `${days} day free trial` : 'a free trial';
    const slotsText = hasSlots
      ? ` (only ${slots} slot${slots === 1 ? '' : 's'} left)`
      : '';

    return {
      title: 'Exclusive trial 🎁',
      body: username
        ? `${username} wants to offer you ${daysText}${slotsText}`
        : hasDays
          ? `${daysText} is available now`
          : 'A free trial is available now',
    };
  }

  if (type === 'subscription' && eventType === 'subscription_success') {
    return {
      title: 'Successfully Subscribed',
      body: username
        ? `Your subscription to ${username} is now active`
        : 'Your subscription is now active',
    };
  }

  if (
    type === 'creator_priority_messages' ||
    eventType === 'unread_priority_messages_banner'
  ) {
    return {
      title: 'Priority Messages',
      body: 'You have unread priority messages waiting for a reply',
    };
  }

  if (
    type === 'creator_trending' ||
    eventType === 'trending_profile_started'
  ) {
    return {
      title: 'Your profile is trending 🔥',
      body: 'More subscribers are now discovering your profile',
    };
  }

  if (type === 'trending' || eventType === 'trending_profile') {
    return {
      title: username ? `${username} is trending 🔥` : 'Trending creator 🔥',
      body: 'Your new obsession is one tap away',
    };
  }

  if (type === 'new_message' || eventType === 'message_received') {
    return {
      title: username ? `${username} sent you a message 💬` : 'New message 💬',
      body: '',
    };
  }

  return {
    title: PUSH_CONFIG.DEFAULT_TITLE,
    body: '',
  };
};

const mapPushToActivityType = ({ type, eventType, callType }) => {
  const normalizedType = String(type || '').toLowerCase();

  if (normalizedType === 'ppv') return 'ppv';
  if (normalizedType === 'bundles' || normalizedType === 'bundle') return 'bundles';
  if (normalizedType === 'unlock') return 'unlock';
  if (normalizedType === 'new_message' || eventType === 'message_received') {
    return 'new_message';
  }
  if (normalizedType === 'purchase_credits') return 'purchase_credits';
  if (normalizedType === 'credits') return 'credits';

  if (String(type || '').toLowerCase() === 'incoming_call_request') {
    const ct = String(callType || '').toLowerCase();
    if (ct === 'video') return 'incoming_call_video';
    return 'incoming_call_voice';
  }

  if (eventType === 'call_credits' || type === 'call_credits') {
    return 'credits';
  }

  if (eventType === 'tip_received' || type === 'payment_tip' || type === 'tip' || type === 'creator_sales_tips') {
    return 'tip';
  }

  if (eventType === 'feed_content_sold') {
    return 'feed_content_sold';
  }

  if (eventType === 'feed_bundle_sold') {
    return 'feed_bundle_sold';
  }

  if (eventType === 'message_bundle_sold') {
    return 'message_bundle_sold';
  }

  if (eventType === 'message_content_sold') {
    return 'message_content_sold';
  }

  if (eventType === 'new_follower' || type === 'creator_followers') {
    return 'new_follower';
  }

  if (eventType === 'tagged_in_post' || type === 'creator_tags') {
    return 'tag';
  }

  if (type === 'new_content') {
    return 'bundles';
  }

  if (
    eventType === 'subscription_success' ||
    eventType === 'new_subscriber' ||
    type === 'subscription'
  ) {
    return 'subscription';
  }

  if (
    DISCOUNT_PROMOTION_EVENTS.has(eventType) ||
    type === 'discount_active' ||
    FREE_TRIAL_PROMOTION_EVENTS.has(eventType) ||
    type === 'promotion'
  ) {
    return DISCOUNT_PROMOTION_EVENTS.has(eventType) || type === 'discount_active'
      ? 'discount'
      : 'free_trial';
  }

  if (
    normalizedType === 'creator_priority_messages' ||
    eventType === 'unread_priority_messages_banner'
  ) {
    return 'priority_messages';
  }

  if (
    normalizedType === 'trending' ||
    normalizedType === 'creator_trending' ||
    eventType === 'trending_profile' ||
    eventType === 'trending_profile_started'
  ) {
    return 'trending';
  }

  return 'credits';
};

const isUnlockedContentEvent = ({ type, eventType, pushData }) => {
  if (eventType === 'content_unlocked' || type === 'content_unlocked') {
    return true;
  }

  const isLockedContentFlag = String(pushData?.lock_content).toLowerCase() === 'true';
  const fileType = String(pushData?.type_file || pushData?.content_type || '').toLowerCase();

  return isLockedContentFlag && (fileType === 'bundle' || fileType === 'content');
};

const resolvePushAvatar = (pushData = {}) => {
  const avatar =
    pushData?.sender_avatar ||
    pushData?.senderAvatar ||
    pushData?.sender_profile_picture ||
    pushData?.senderProfilePicture ||
    pushData?.from_avatar ||
    pushData?.creator_avatar ||
    pushData?.profile_picture ||
    pushData?.avatar ||
    pushData?.fromAvatar ||
    pushData?.creatorAvatar ||
    null;

  if (!avatar || avatar === 'default/default-user.jpeg') {
    return null;
  }

  return avatar;
};

const PUSH_TYPES_WITH_AVATAR = new Set([
  'tag',
  'new_follower',
  'new_subscribers',
  'subscription',
  'free_trial',
  'discount',
  'tip',
  'trending',
]);

const PUSH_SALES_TYPES_WITH_AVATAR = new Set([
  'feed_content_sold',
  'feed_bundle_sold',
  'message_bundle_sold',
  'message_content_sold',
]);

export const normalizeForegroundPushDetail = (detail = {}) => {
  const pushData = flattenPushDataPayload({ data: detail?.data || {}, ...detail });

  const type = detail?.type || pushData?.type || '';
  const eventType =
    pushData?.eventType ||
    pushData?.notificationType ||
    '';
  const unlockedContentEvent = isUnlockedContentEvent({ type, eventType, pushData });
  const discountEvent = eventType === 'discount_active' || type === 'discount_active';
  const activityType = mapPushToActivityType({
    type,
    eventType,
    callType: pushData?.callType,
  });
  const avatar = resolvePushAvatar(pushData);
  const username = readUsernameFromPayload(pushData);
  const fallback = buildFallbackPushContent({
    type: unlockedContentEvent ? 'content_unlocked' : type,
    eventType: unlockedContentEvent ? 'content_unlocked' : eventType,
    username,
    amount: pushData?.amount,
    trialDays: pushData?.trialDays,
    offerSlots: pushData?.offerSlots,
    discountedPrice: pushData?.discountedPrice,
    percentOff: pushData?.percentOff,
  });
  const isTipEvent =
    type === 'payment_tip' ||
    type === 'tip' ||
    type === 'creator_sales_tips' ||
    eventType === 'tip_received' ||
    eventType === 'tip_sent';
  const isCallCreditsEvent = eventType === 'call_credits' || type === 'call_credits';
  const isUnlockedEvent = unlockedContentEvent;
  const isDiscountEvent = discountEvent;
  const isProfileUpdatedEvent =
    eventType === 'profile_updated' || type === 'profile';
  const isMessageReceivedEvent =
    eventType === 'message_received' || type === 'new_message';

  const isCreatorTrendingEvent =
    type === 'creator_trending' || eventType === 'trending_profile_started';
  const shouldPreferFallbackCopy =
    (isTipEvent && !isCallCreditsEvent) ||
    isUnlockedEvent ||
    isDiscountEvent ||
    isCreatorTrendingEvent;
  const payloadForCopy = { data: detail?.data || {}, ...detail };
  const explicitTitle = resolvePushTitle(payloadForCopy, pushData);

  const payloadTitle =
    explicitTitle ||
    (isCreatorTrendingEvent ? fallback.title : '') ||
    fallback.title ||
    PUSH_CONFIG.DEFAULT_TITLE;
  const resolvedBody = resolvePushBody(payloadForCopy, pushData);
  const rawBody = shouldPreferFallbackCopy
    ? fallback.body || resolvedBody
    : resolvedBody || fallback.body || '';
  const callForegroundCopy = buildCallForegroundPushCopy({
    pushData,
    username,
    resolvedBody: rawBody,
  });
  const normalizedTitle = callForegroundCopy?.title || payloadTitle;
  const normalizedBody = normalizePushNotificationBody(
    callForegroundCopy?.body ?? rawBody,
    pushData
  );

  return {
    title: formatBannerTitle(normalizedTitle),
    body: normalizedBody,
    username: stripUsernameAt(username),
    link:
      detail?.link ||
      resolvePushLink({ data: detail?.data || {}, ...detail }, PUSH_CONFIG.DEFAULT_LINK),
    activityType,
    avatar,
    useAvatar:
      isProfileUpdatedEvent ||
      isMessageReceivedEvent ||
      (Boolean(avatar) &&
        (PUSH_TYPES_WITH_AVATAR.has(activityType) ||
          PUSH_SALES_TYPES_WITH_AVATAR.has(activityType))),
    tag: detail?.tag || pushData?.notificationId || `push-toast-${Date.now()}`,
  };
};

